import { useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { addToast } from '../../../../../store/toast/toastSlice'
import { useGetAccountQuery } from '../../../../../store/accountUserSettings/accountUserSettings'
import { type CheckpointRequest, useCreateCheckpointMutation, useGetCheckpointQuery, useUpdateCheckpointMutation } from '../../../../../store/sync/sync'
import { getAccountCRMIntegrationConfig } from '../../../../../models/Account'
import { type IntegrationServiceId } from '../../../../../common/integrations'
import { type SalesforceSettingsFormValues } from './models'

/**
 * Shared plumbing for the per-user auto-sync checkpoints behind the Salesforce-based settings tabs.
 * `active === null` means no checkpoint exists yet — the sync was never enabled — so the first "on" creates one.
 */
const useSalesforceBasedSyncCheckpoint = (serviceId: IntegrationServiceId, object: string, settingLabel: string) => {
  const dispatch = useAppDispatch()
  const accountId = useAppSelector(selectAccountId)
  const { data: account, isLoading: isAccountLoading, isFetching: isAccountFetching } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: 10 })
  const checkpointRequest = {
    accountId,
    level: 'user',
    connector: serviceId,
    direction: 'inbound',
    object,
  } as CheckpointRequest
  const { data: checkpoint, isLoading: isCheckpointLoading } = useGetCheckpointQuery(checkpointRequest, { refetchOnMountOrArgChange: 10 })
  const [createCheckpoint] = useCreateCheckpointMutation()
  const [updateCheckpoint] = useUpdateCheckpointMutation()
  const [isUpdating, setIsUpdating] = useState<boolean>(false)

  const active = checkpoint?.data?.active ?? null
  const isSyncDisabled = account?.data ? (getAccountCRMIntegrationConfig(account.data, serviceId)?.isSyncDisabled ?? false) : true

  // The quiet variant leaves `isSaving` alone: the onboarding flow drives its own busy state.
  const setActiveQuietly = async (value: boolean): Promise<boolean> => {
    try {
      if (active == null) {
        if (value) {
          await createCheckpoint(checkpointRequest).unwrap()
        }
      } else {
        await updateCheckpoint({ ...checkpointRequest, active: value }).unwrap()
      }
      return true
    } catch (e) {
      console.error(e)
      dispatch(addToast({ variant: 'error', title: 'Saving failed', messages: [`Your ${settingLabel} setting was not saved. Please try again.`] }))
      return false
    }
  }

  const setActive = async (value: boolean): Promise<boolean> => {
    setIsUpdating(true)
    const saved = await setActiveQuietly(value)
    setIsUpdating(false)
    return saved
  }

  return {
    active,
    isSyncDisabled,
    isAccountFetching,
    isFetching: isCheckpointLoading || isAccountLoading,
    isSaving: isUpdating,
    setActive,
    setActiveQuietly,
  }
}

export const useSalesforceBasedContactSync = (serviceId: IntegrationServiceId) => {
  const { active, setActive, setActiveQuietly, ...rest } = useSalesforceBasedSyncCheckpoint(serviceId, 'contact_operation_hashed', 'contact sync')

  return {
    ...rest,
    autoSync: active,
    save: (data: SalesforceSettingsFormValues) => setActive(data.inbound.contact.autoSync),
    saveAutoSync: setActiveQuietly,
  }
}

export const useSalesforceBasedCalendarSync = (serviceId: IntegrationServiceId) => {
  const { active, setActive, setActiveQuietly, ...rest } = useSalesforceBasedSyncCheckpoint(serviceId, 'event_operation_hashed', 'calendar sync')

  return {
    ...rest,
    enabled: active,
    save: setActive,
    saveQuietly: setActiveQuietly,
  }
}
